import { getGuidedFlowContentIssues } from './FAQFlow';
import type { GuidedFlow, GuidedFlowAnswer, GuidedFlowNode } from './FAQFlow';
import type { HelpArticle, HelpTopic } from './FAQContent';

export const helpCenterConfigPath = 'publicAppConfig/helpCenter';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function readStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  const strings = value.filter((item): item is string => typeof item === 'string');
  return strings.length === value.length ? strings : null;
}

function normalizeArticle(value: unknown): HelpArticle | null {
  if (!isRecord(value)) {
    return null;
  }

  const key = readString(value.key);
  const title = readString(value.title);
  const description = readStringArray(value.description);

  if (!key || !title || !description || description.length === 0) {
    return null;
  }

  return {
    key,
    title,
    description,
    videoUrl: readOptionalString(value.videoUrl),
    searchKeywords: readStringArray(value.searchKeywords) || undefined,
  };
}

function normalizeAnswer(value: unknown): GuidedFlowAnswer | null {
  if (!isRecord(value)) {
    return null;
  }

  const key = readString(value.key);
  const label = readString(value.label);
  const nextNodeId = readString(value.nextNodeId);

  if (!key || !label || !nextNodeId) {
    return null;
  }

  return {
    key,
    label,
    description: readOptionalString(value.description),
    searchKeywords: readStringArray(value.searchKeywords) || undefined,
    nextNodeId,
  };
}

function normalizeNode(value: unknown): GuidedFlowNode | null {
  if (!isRecord(value)) {
    return null;
  }

  const id = readString(value.id);
  const type = readString(value.type);
  const title = readString(value.title);

  if (!id || !title) {
    return null;
  }

  if (type === 'question') {
    const question = readString(value.question);
    const answers = Array.isArray(value.answers)
      ? value.answers
          .map(normalizeAnswer)
          .filter((answer): answer is GuidedFlowAnswer => Boolean(answer))
      : [];

    if (!question || answers.length === 0) {
      return null;
    }

    return {
      id,
      type,
      title,
      question,
      helperText: readOptionalString(value.helperText),
      searchKeywords: readStringArray(value.searchKeywords) || undefined,
      answers,
    };
  }

  if (type === 'result') {
    const summary = readString(value.summary);
    const nextSteps = readStringArray(value.nextSteps);

    if (!summary || !nextSteps || nextSteps.length === 0) {
      return null;
    }

    return {
      id,
      type,
      title,
      summary,
      nextSteps,
      supportText: readOptionalString(value.supportText),
      searchKeywords: readStringArray(value.searchKeywords) || undefined,
    };
  }

  return null;
}

function normalizeFlow(value: unknown): GuidedFlow | null {
  if (!isRecord(value)) {
    return null;
  }

  const key = readString(value.key);
  const title = readString(value.title);
  const description = readString(value.description);
  const startNodeId = readString(value.startNodeId);
  const nodesInput = value.nodes;
  const rawNodes = isRecord(nodesInput)
    ? Object.keys(nodesInput).map((nodeKey) => nodesInput[nodeKey])
    : null;

  if (!key || !title || !description || !startNodeId || !rawNodes) {
    return null;
  }

  const nodes = rawNodes
    .map(normalizeNode)
    .filter((node): node is GuidedFlowNode => Boolean(node))
    .reduce<Record<string, GuidedFlowNode>>((result, node) => {
      return {
        ...result,
        [node.id]: node,
      };
    }, {});

  const flow: GuidedFlow = {
    key,
    title,
    description,
    searchKeywords: readStringArray(value.searchKeywords) || undefined,
    startNodeId,
    nodes,
  };

  return getGuidedFlowContentIssues(flow).length === 0 ? flow : null;
}

function normalizeTopic(value: unknown): HelpTopic | null {
  if (!isRecord(value)) {
    return null;
  }

  const key = readString(value.key);
  const title = readString(value.title);
  const summary = readString(value.summary);

  if (!key || !title || !summary) {
    return null;
  }

  const articles = Array.isArray(value.articles)
    ? value.articles
        .map(normalizeArticle)
        .filter((article): article is HelpArticle => Boolean(article))
    : undefined;
  const guidedFlows = Array.isArray(value.guidedFlows)
    ? value.guidedFlows.map(normalizeFlow).filter((flow): flow is GuidedFlow => Boolean(flow))
    : undefined;
  const hasArticles = Boolean(articles && articles.length > 0);
  const hasGuidedFlows = Boolean(guidedFlows && guidedFlows.length > 0);

  if (!hasArticles && !hasGuidedFlows) {
    return null;
  }

  return {
    key,
    title,
    summary,
    searchKeywords: readStringArray(value.searchKeywords) || undefined,
    articles: hasArticles ? articles : undefined,
    guidedFlows: hasGuidedFlows ? guidedFlows : undefined,
  };
}

export function normalizeHelpCenterTopics(value: unknown): HelpTopic[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  const topics = value.map(normalizeTopic).filter((topic): topic is HelpTopic => Boolean(topic));

  return topics.length > 0 ? topics : null;
}
